import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useVoiceStreaming, type VoiceStreamStatus } from './useVoiceStreaming'

export type InlineFieldSpeechStatus = 'idle' | 'listening' | 'error'

/** AWS Transcribe is configured for French only (fr-FR). */
const AWS_TRANSCRIBE_LOCALE = 'fr-FR'

function awsTranscribeLanguageConfig(): { preferred: string; options: string[] } {
  return { preferred: AWS_TRANSCRIBE_LOCALE, options: [AWS_TRANSCRIBE_LOCALE] }
}

function isVoiceBusy(status: VoiceStreamStatus): boolean {
  return (
    status === 'requesting_permission' ||
    status === 'connecting' ||
    status === 'recording' ||
    status === 'transcribing'
  )
}

function mapVoiceStatus(status: VoiceStreamStatus): InlineFieldSpeechStatus {
  if (status === 'error') return 'error'
  if (isVoiceBusy(status)) return 'listening'
  return 'idle'
}

type Args = {
  websocketUrl: string
  apiBaseUrl?: string
  transcribeMode?: 1 | 2
  getMedicalLinkCredentials?: () => {
    piBearerToken: string
    loggedUserId: string
    medCenterId: string
    senderFbUid?: string
  }
  clientId?: string
  tenantId?: string
  sessionId?: string
  languageHint?: string
  disabled?: boolean
  /** Main composer mic or send in progress — do not start inline capture. */
  blocked?: boolean
  onTranscript: (text: string) => void
  onError?: (message: string) => void
  onBusyChange?: (busy: boolean) => void
}

/**
 * Single-utterance AWS Transcribe (streaming WebSocket) for inline field edits.
 * Same path as the main composer mic; does not run the chat command pipeline.
 */
export function useInlineFieldSpeechRecognition({
  websocketUrl,
  apiBaseUrl,
  transcribeMode = 1,
  getMedicalLinkCredentials,
  clientId,
  tenantId,
  sessionId,
  disabled,
  blocked,
  onTranscript,
  onError,
  onBusyChange,
}: Args) {
  const onTranscriptRef = useRef(onTranscript)
  onTranscriptRef.current = onTranscript
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const [error, setError] = useState<string | null>(null)
  const { preferred, options } = useMemo(
    () => awsTranscribeLanguageConfig(),
    [],
  )

  const voice = useVoiceStreaming({
    websocketUrl,
    apiBaseUrl,
    transcribeMode,
    clientId,
    tenantId,
    sessionId,
    getMedicalLinkCredentials,
    languageOptions: options,
    preferredLanguage: preferred,
    transcriptionOnly: true,
    silenceAutoStopMs: 1800,
    onPartial: (text) => {
      if (text.trim()) onTranscriptRef.current(text)
    },
    onFinal: (text) => {
      const trimmed = text.trim()
      if (trimmed) onTranscriptRef.current(trimmed)
    },
    onError: (msg) => {
      setError(msg)
      onErrorRef.current?.(msg)
    },
  })

  const status = mapVoiceStatus(voice.status)
  const supported =
    transcribeMode === 2 ? Boolean((apiBaseUrl ?? '').trim()) : Boolean(websocketUrl.trim())

  useEffect(() => {
    onBusyChange?.(isVoiceBusy(voice.status))
  }, [voice.status, onBusyChange])

  useEffect(() => {
    return () => {
      onBusyChange?.(false)
    }
  }, [onBusyChange])

  const toggle = useCallback(() => {
    if (disabled || !supported) return
    if (isVoiceBusy(voice.status)) {
      if (voice.status === 'error') setError(null)
      voice.toggleMic()
      return
    }
    if (blocked) return
    if (voice.status === 'error') setError(null)
    voice.toggleMic()
  }, [disabled, blocked, supported, voice])

  return {
    supported,
    status,
    statusHint: voice.statusHint,
    error,
    toggle,
  }
}
